import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { computeIVA, fmtMXN } from "@/lib/iva";

export default function AdminPOS() {
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [cart, setCart] = useState([]);
  const [client, setClient] = useState({ name: "", email: "" });
  const [linkedAppt, setLinkedAppt] = useState("");
  const [payment, setPayment] = useState("cash");
  const [discount, setDiscount] = useState(0);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/appointments").then((r) => setAppointments(r.data.filter((a) => a.status === "confirmed")));
  }, []);

  function addItem(kind, ref) {
    const existing = cart.find((c) => c.kind === kind && c.ref_id === ref.id);
    if (existing) {
      setCart(cart.map((c) => (c === existing ? { ...c, qty: c.qty + 1 } : c)));
    } else {
      setCart([...cart, { kind, ref_id: ref.id, name: ref.name, qty: 1, unit_price: ref.price }]);
    }
  }

  function removeItem(idx) { setCart(cart.filter((_, i) => i !== idx)); }
  function changeQty(idx, q) { setCart(cart.map((c, i) => (i === idx ? { ...c, qty: Math.max(1, Number(q) || 1) } : c))); }

  const subtotal = useMemo(() => cart.reduce((sum, i) => sum + i.qty * i.unit_price, 0), [cart]);

  const linkedAppointment = useMemo(
    () => appointments.find((a) => a.id === linkedAppt),
    [appointments, linkedAppt]
  );
  const depositCredit = linkedAppointment?.payment_status === "paid"
    ? Number(linkedAppointment.deposit_amount || 0)
    : 0;
  const total = Math.max(subtotal - Number(discount || 0) - depositCredit, 0);

  function pickAppointment(apptId) {
    setLinkedAppt(apptId);
    if (!apptId || apptId === "none") { setLinkedAppt(""); return; }
    const a = appointments.find((x) => x.id === apptId);
    if (!a) return;
    setClient({ name: a.client_name, email: a.client_email });
    if (!cart.find((c) => c.kind === "service" && c.ref_id === a.service_id)) {
      setCart((c) => [...c, { kind: "service", ref_id: a.service_id, name: a.service_name, qty: 1, unit_price: a.service_price }]);
    }
  }

  async function checkout() {
    if (cart.length === 0) return toast.error("Agrega ítems");
    if (!client.name) return toast.error("Falta nombre del cliente");
    try {
      await api.post("/sales", {
        appointment_id: linkedAppt || null,
        client_name: client.name,
        client_email: client.email,
        items: cart,
        payment_method: payment,
        discount: Number(discount || 0),
      });
      toast.success(`Venta registrada por $${total.toFixed(2)}`);
      setCart([]); setClient({ name: "", email: "" }); setLinkedAppt(""); setDiscount(0);
    } catch { toast.error("Error al cobrar"); }
  }

  return (
    <div>
      <div className="mb-8">
        <p className="overline">Caja</p>
        <h1 className="font-display text-4xl font-light tracking-tight mt-3">Punto de venta</h1>
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-6">
        <div className="space-y-6">
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <p className="overline mb-4">Servicios</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {services.map((s) => (
                  <button key={s.id} onClick={() => addItem("service", s)} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-4 py-3 text-left hover:border-primary card-hover" data-testid={`pos-add-service-${s.id}`}>
                    <div>
                      <p className="font-medium text-sm">{s.name}</p>
                      <p className="text-xs text-slate-500">{s.duration_min} min</p>
                    </div>
                    <span className="text-sm">${s.price.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <p className="overline mb-4">Productos</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {products.map((p) => (
                  <button key={p.id} onClick={() => addItem("product", p)} className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-4 py-3 text-left hover:border-primary card-hover" data-testid={`pos-add-product-${p.id}`}>
                    <div>
                      <p className="font-medium text-sm">{p.name}</p>
                      <p className="text-xs text-slate-500">Stock: {p.stock}</p>
                    </div>
                    <span className="text-sm">${p.price.toFixed(2)}</span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-slate-200 lg:sticky lg:top-6 self-start" data-testid="pos-cart-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <p className="overline">Carrito</p>
              <Receipt className="h-4 w-4 text-slate-400" />
            </div>

            <div className="space-y-2 mb-4">
              <Label className="text-xs">Vincular cita</Label>
              <Select value={linkedAppt || "none"} onValueChange={pickAppointment}>
                <SelectTrigger data-testid="pos-link-appt"><SelectValue placeholder="(Sin cita)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin cita</SelectItem>
                  {appointments.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.date} {a.time} · {a.client_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 mb-4">
              <Input placeholder="Cliente" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} data-testid="pos-client-name" />
              <Input placeholder="Correo (opcional)" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} data-testid="pos-client-email" />
            </div>

            <div className="border-t border-slate-200 pt-4 space-y-2 max-h-64 overflow-auto" data-testid="pos-cart-items">
              {cart.length === 0 && <p className="text-sm text-slate-500">Sin ítems</p>}
              {cart.map((it, i) => (
                <div key={`${it.kind}-${it.ref_id}`} className="flex items-center gap-2 text-sm">
                  <div className="flex-1">
                    <p className="font-medium">{it.name}</p>
                    <p className="text-xs text-slate-500">{it.kind === "service" ? "Servicio" : "Producto"} · ${it.unit_price.toFixed(2)}</p>
                  </div>
                  <Input type="number" min={1} value={it.qty} onChange={(e) => changeQty(i, e.target.value)} className="w-16 h-8" data-testid={`pos-qty-${i}`} />
                  <button onClick={() => removeItem(i)} className="text-slate-400 hover:text-red-700" data-testid={`pos-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-200 mt-4 pt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal (bruto)</span><span>{fmtMXN(subtotal)}</span></div>
              {depositCredit > 0 && (
                <div className="flex justify-between font-medium" data-testid="pos-deposit-applied" style={{ color: "#1E3A8A" }}>
                  <span>Depósito ya pagado</span>
                  <span>− {fmtMXN(depositCredit)}</span>
                </div>
              )}
              <div className="flex justify-between items-center"><span className="text-slate-500">Descuento</span>
                <Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-24 h-8" data-testid="pos-discount" />
              </div>
              <div className="flex justify-between"><span className="text-slate-500">Pago</span>
                <Select value={payment} onValueChange={setPayment}>
                  <SelectTrigger className="w-32 h-8" data-testid="pos-payment-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Efectivo</SelectItem>
                    <SelectItem value="card">Tarjeta</SelectItem>
                    <SelectItem value="transfer">Transferencia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {(() => {
              const b = computeIVA(total, true);
              return (
                <div className="mt-4 rounded-md p-4 text-slate-50" style={{ backgroundColor: "#1E3A8A" }} data-testid="pos-total">
                  <div className="flex items-center justify-between">
                    <p className="overline text-slate-300">Total</p>
                    <p className="font-display text-3xl font-light">{fmtMXN(b.total)}</p>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/15 grid grid-cols-2 gap-2 text-xs text-slate-300" data-testid="pos-iva-breakdown">
                    <div>Subtotal sin IVA<br/><span className="text-white text-sm font-medium">{fmtMXN(b.subtotal)}</span></div>
                    <div className="text-right">IVA (16%)<br/><span className="text-white text-sm font-medium">{fmtMXN(b.iva)}</span></div>
                  </div>
                </div>
              );
            })()}

            <Button className="w-full mt-4" disabled={cart.length === 0} onClick={checkout} data-testid="pos-checkout-btn">
              Cobrar
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
